/**
 * Zod Validation Schemas for API Responses
 * Provides runtime type validation to prevent injection attacks and type confusion
 */

import { z } from 'zod';

// ============================================================================
// Base Schemas
// ============================================================================

export const BaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
  error: z.string().optional(),
});

export const PaginationSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  hasMore: z.boolean().optional(),
});

// ============================================================================
// Connector Schemas
// ============================================================================

export const ConnectorCredentialsSchema = z.record(z.string(), z.string());

export const ConnectorConfigSchema = z.record(z.string(), z.unknown());

export const ConnectorTypeSchema = z.object({
  id: z.string(),
  name: z.string(),
  category: z.string(),
  description: z.string().optional(),
  icon: z.string().optional(),
  config_schema: z.record(z.string(), z.unknown()).optional(),
  supports_health_check: z.boolean().optional(),
  supports_test_connection: z.boolean().optional(),
});

export const ConnectorSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string().optional(), // Backend returns connector_type, not type - make optional
  type_id: z.string().optional(),
  connector_type: z.string().optional(), // Backend uses connector_type
  category: z.string().optional(),
  status: z.string(), // Backend uses String(20), not enum - can be any status value
  config: ConnectorConfigSchema.optional().nullable(), // Config can be null or missing
  credentials: ConnectorCredentialsSchema.optional().nullable(), // Credentials can be null or missing
  ingestion_enabled: z.boolean().optional(),
  // last_sync removed - backend doesn't return this field
  created_at: z.string().optional().nullable(), // Accept any string format, backend returns ISO datetime or None
  updated_at: z.string().optional().nullable(), // Accept any string format, backend returns ISO datetime or None
  tenant_id: z.string().optional(), // Made optional - backend doesn't return it for security
  health_status: z.string().optional(),
  error_message: z.string().optional(),
}).passthrough(); // Allow extra fields from backend (description, connector_subtype, display_name, etc.)

export const ConnectorHealthSchema = z.object({
  status: z.enum(['healthy', 'degraded', 'unhealthy', 'unknown']),
  last_check: z.string().datetime().optional(),
  uptime_percentage: z.number().min(0).max(100).optional(),
  error_count: z.number().int().nonnegative().optional(),
  latency_ms: z.number().nonnegative().optional(),
  details: z.record(z.string(), z.unknown()).optional(),
  history: z.array(z.any()).optional(),
});

export const ConnectorTestResultSchema = z.object({
  success: z.boolean(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
  timestamp: z.string().datetime().optional(),
  error: z.string().optional(),
});

export const ConnectorLogSchema = z.object({
  id: z.string(),
  connector_id: z.string(),
  timestamp: z.string().datetime(),
  level: z.enum(['info', 'warning', 'error', 'debug']),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// Alert Schemas
// ============================================================================

export const AlertSeveritySchema = z.enum(['critical', 'high', 'medium', 'low', 'info']);

export const AlertStatusSchema = z.enum([
  'open',
  'in_progress',
  'resolved',
  'closed',
  'false_positive',
  'requires_review'
]);

export const MitreTechniqueSchema = z.object({
  technique_id: z.string(),
  name: z.string(),
  confidence: z.number().min(0).max(1),
  category: z.string(),
});

export const RecommendedActionSchema = z.object({
  id: z.string(),
  action_type: z.string(),
  priority: z.enum(['critical', 'high', 'medium', 'low']),
  description: z.string(),
  automation_available: z.boolean(),
  estimated_time: z.string(),
  prerequisites: z.array(z.string()),
  impact_level: z.enum(['high', 'medium', 'low']),
  executed: z.boolean(),
  execution_status: z.string().optional(),
});

export const EnrichmentSummarySchema = z.object({
  threat_intelligence: z.record(z.string(), z.unknown()).optional(),
  compliance_impact: z.record(z.string(), z.unknown()).optional(),
  rule_analysis: z.record(z.string(), z.unknown()).optional(),
  behavioral_patterns: z.record(z.string(), z.unknown()).optional(),
});

export const AlertSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string().optional(),
  severity: AlertSeveritySchema,
  status: AlertStatusSchema,
  category: z.string().optional(),
  source: z.string().optional(),
  timestamp: z.string().datetime().optional(),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
  enrichment_status: z.string().optional(),
  duration_ms: z.number().nonnegative().optional(),
  enrichment_summary: EnrichmentSummarySchema.optional(),
  cross_device_score: z.number().min(0).max(1).optional(),
  evasion_resistance: z.number().min(0).max(1).optional(),
  amplified_signal: z.number().min(0).max(1).optional(),
  ensemble_consensus: z.number().min(0).max(1).optional(),
  relationship_complexity: z.number().min(0).max(1).optional(),
  temporal_correlation: z.number().min(0).max(1).optional(),
  behavioral_anomaly: z.number().min(0).max(1).optional(),
  sequence_anomaly: z.number().min(0).max(1).optional(),
  decision_classification: z.string().optional(),
  decision_confidence: z.enum(['high', 'medium', 'low']).optional(),
  decision_reason: z.string().optional(),
  decision_timestamp: z.string().datetime().optional(),
  recommended_action: z.string().optional(),
  decision_score: z.number().min(0).max(1).optional(),
  recommended_actions: z.array(RecommendedActionSchema).optional(),
  mitre_techniques: z.array(MitreTechniqueSchema).optional(),
});

// ============================================================================
// Executive Dashboard Schemas
// ============================================================================

export const EventSchema = z.object({
  id: z.string(),
  title: z.string(),
  timestamp: z.string(),
  severity: AlertSeveritySchema,
  category: z.string(),
  description: z.string(),
  source: z.string(),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
});

export const InsightSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  category: z.string(),
  impact_level: z.enum(['high', 'medium', 'low']),
  recommended_action: z.string(),
  due_date: z.string().optional(),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
});

export const HeatmapCellSchema = z.object({
  business_unit: z.string(),
  risk_score: z.number().min(0).max(100),
  trend: z.enum(['increasing', 'decreasing', 'stable']),
  alert_count: z.number().int().nonnegative(),
  risk_level: z.enum(['critical', 'high', 'medium', 'low']),
});

export const GovernanceCalloutSchema = z.object({
  id: z.string(),
  title: z.string(),
  description: z.string(),
  due_date: z.string(),
  priority: z.enum(['critical', 'high', 'medium', 'low']),
  status: z.enum(['pending', 'in_progress', 'completed', 'overdue']),
  owner: z.string().optional(),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
});

export const StatusResponseSchema = z.object({
  last_update: z.string(),
  peer_count: z.number(),
  system_health: z.enum(['healthy', 'degraded', 'critical']),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
  update_frequency_minutes: z.number(),
});

export const ExportResultSchema = z.object({
  id: z.string(),
  format: z.string(),
  url: z.string(),
});

export const PeerBenchmarkSchema = z.object({
  businessUnit: z.string(),
  score: z.number().min(0).max(100),
  percentile: z.number().min(0).max(100).optional(),
});

export const DrilldownLinkSchema = z.object({
  label: z.string(),
  url: z.string(),
});

export const CompliancePackSchema = z.object({
  id: z.string(),
  name: z.string(),
  url: z.string(),
});

// ============================================================================
// User & Tenant Schemas
// ============================================================================

export const UserRoleSchema = z.enum([
  'admin',
  'analyst',
  'executive',
  'viewer',
  'platform_admin'
]);

export const UserSchema = z.object({
  id: z.string(),
  username: z.string(),
  email: z.string().email(),
  roles: z.array(UserRoleSchema),
  tenant_id: z.string().optional(), // Made optional - backend doesn't return for security
  first_name: z.string().optional(),
  last_name: z.string().optional(),
  created_at: z.string().datetime().optional(),
  last_login: z.string().datetime().optional(),
});

export const TenantSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  status: z.enum(['active', 'inactive', 'suspended']),
  created_at: z.string().datetime().optional(),
  settings: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// Graph & Topology Schemas
// ============================================================================

export const EntityPropertiesSchema = z.record(z.string(), z.unknown());

export const EntitySchema = z.object({
  id: z.string(),
  type: z.string(),
  label: z.string().optional(),
  properties: EntityPropertiesSchema,
  created_at: z.string().datetime().optional(),
});

export const RelationshipSchema = z.object({
  id: z.string(),
  source: z.string(),
  target: z.string(),
  type: z.string(),
  properties: z.record(z.string(), z.unknown()).optional(),
  created_at: z.string().datetime().optional(),
});

export const GraphDataSchema = z.object({
  nodes: z.array(EntitySchema),
  edges: z.array(RelationshipSchema),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// ML Model Schemas
// ============================================================================

export const MLModelSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.enum(['active', 'training', 'inactive', 'error']),
  version: z.string(),
  accuracy: z.number().min(0).max(1).optional(),
  created_at: z.string().datetime().optional(),
  last_trained: z.string().datetime().optional(),
  parameters: z.record(z.string(), z.unknown()).optional(),
});

// ============================================================================
// Response Wrapper Schemas
// ============================================================================

export const ApiResponseSchema = <T extends z.ZodTypeAny>(dataSchema: T) =>
  z.object({
    success: z.boolean(),
    data: dataSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  });

export const PaginatedResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    success: z.boolean(),
    data: z.array(itemSchema),
    pagination: PaginationSchema.optional(),
    error: z.string().optional(),
    message: z.string().optional(),
  });

// ============================================================================
// Array Response Schemas
// ============================================================================

export const ConnectorsResponseSchema = z.array(ConnectorSchema);
export const ConnectorTypesResponseSchema = z.array(ConnectorTypeSchema);
export const AlertsResponseSchema = z.array(AlertSchema);
export const EventsResponseSchema = z.array(EventSchema);
export const InsightsResponseSchema = z.array(InsightSchema);
export const HeatmapResponseSchema = z.array(HeatmapCellSchema);
export const CalloutsResponseSchema = z.array(GovernanceCalloutSchema);
export const ExportsResponseSchema = z.array(ExportResultSchema);
export const PeerBenchmarksResponseSchema = z.array(PeerBenchmarkSchema);
export const DrilldownLinksResponseSchema = z.array(DrilldownLinkSchema);
export const CompliancePacksResponseSchema = z.array(CompliancePackSchema);
export const ConnectorLogsResponseSchema = z.array(ConnectorLogSchema);

// ============================================================================
// Type Exports (inferred from schemas)
// ============================================================================

export type Connector = z.infer<typeof ConnectorSchema>;
export type ConnectorType = z.infer<typeof ConnectorTypeSchema>;
export type ConnectorHealth = z.infer<typeof ConnectorHealthSchema>;
export type ConnectorTestResult = z.infer<typeof ConnectorTestResultSchema>;
export type ConnectorLog = z.infer<typeof ConnectorLogSchema>;
export type Alert = z.infer<typeof AlertSchema>;
export type Event = z.infer<typeof EventSchema>;
export type Insight = z.infer<typeof InsightSchema>;
export type HeatmapCell = z.infer<typeof HeatmapCellSchema>;
export type GovernanceCallout = z.infer<typeof GovernanceCalloutSchema>;
export type StatusResponse = z.infer<typeof StatusResponseSchema>;
export type ExportResult = z.infer<typeof ExportResultSchema>;
export type PeerBenchmark = z.infer<typeof PeerBenchmarkSchema>;
export type DrilldownLink = z.infer<typeof DrilldownLinkSchema>;
export type CompliancePack = z.infer<typeof CompliancePackSchema>;
export type User = z.infer<typeof UserSchema>;
export type Tenant = z.infer<typeof TenantSchema>;
export type Entity = z.infer<typeof EntitySchema>;
export type Relationship = z.infer<typeof RelationshipSchema>;
export type GraphData = z.infer<typeof GraphDataSchema>;
export type MLModel = z.infer<typeof MLModelSchema>;
export type RecommendedAction = z.infer<typeof RecommendedActionSchema>;
export type MitreTechnique = z.infer<typeof MitreTechniqueSchema>;
